import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Coins, Sparkles, X } from 'lucide-react';
import {
  CreditOperationPreview,
  setCreditConfirmationHandler,
} from '../utils/api';
import { useTranslation } from '../i18n/translations';

export const CreditConfirmationDialog: React.FC = () => {
  const { tx, language } = useTranslation();
  const [operation, setOperation] = useState<CreditOperationPreview | null>(null);
  const resolverRef = useRef<((accepted: boolean) => void) | null>(null);

  useEffect(() => {
    setCreditConfirmationHandler((nextOperation) => new Promise<boolean>((resolve) => {
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOperation(nextOperation);
    }));
    return () => {
      setCreditConfirmationHandler(null);
      resolverRef.current?.(false);
      resolverRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!operation) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [operation]);

  const finish = (accepted: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOperation(null);
    resolve?.(accepted);
  };

  if (!operation) return null;

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';
  const operationLabel = language === 'ar' ? operation.label_ar : operation.label_en;

  return (
    <div className="credit-confirm-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && finish(false)}>
      <section className="credit-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="credit-confirm-title">
        <header>
          <span className="credit-confirm-icon"><Coins /></span>
          <button type="button" onClick={() => finish(false)} aria-label={tx('إغلاق')}><X /></button>
        </header>
        <div className="credit-confirm-copy">
          <span>{tx('تأكيد استخدام الرصيد')}</span>
          <h2 id="credit-confirm-title">{operationLabel}</h2>
          <p>{tx('راجع تكلفة العملية والرصيد المتوقع قبل بدء التنفيذ.')}</p>
        </div>
        <div className="credit-confirm-equation" aria-label={tx('حساب الرصيد بعد العملية')}>
          <div><small>{tx('رصيدك الحالي')}</small><strong>{operation.balance_before.toLocaleString(locale)}</strong></div>
          <b>−</b>
          <div className="is-cost"><small>{tx('تكلفة العملية')}</small><strong>{operation.cost_credits.toLocaleString(locale)}</strong></div>
          <b>=</b>
          <div><small>{tx('الرصيد بعد التنفيذ')}</small><strong>{operation.balance_after.toLocaleString(locale)}</strong></div>
        </div>
        {operation.campaigns_remaining !== null && (
          <div className="credit-confirm-capacity"><Sparkles /><span>{tx('بعد هذه العملية سيبقى رصيدك كافياً لإنشاء')} <strong>{operation.campaigns_remaining.toLocaleString(locale)}</strong> {tx('حملات كاملة تقريباً')}.</span></div>
        )}
        <footer>
          <button type="button" className="btn btn-ghost" onClick={() => finish(false)}>{tx('إلغاء')}</button>
          <button type="button" className="btn btn-primary" onClick={() => finish(true)}>{tx('تأكيد وبدء التنفيذ')} <ArrowLeft className="rtl-mirror" /></button>
        </footer>
      </section>
    </div>
  );
};
